import { useParams } from 'react-router-dom';
import { snapshot, useSnapshot } from 'valtio';
import getStateManager from '../StateManager';
import { useProxySelector } from './useProxySelector';

// Tabs render from `controllerSnapshot`/`parentSnapshot` and write to `controller`/`parent`.
// A snapshot subscribes to exactly the fields read while rendering (including
// reads by child widgets it is passed to), so a tab re-renders when something
// it shows changes, and not on unrelated live data.
//
// Keep the proxies for event handlers, save paths, effects and components that
// subscribe on their own (e.g. useControllerConnectionStatus): a snapshot is
// frozen and cannot be subscribed to.
export function useTabController(params) {
  const routeParams = useParams();
  const { controller, parent } = getStateManager().getControllerForTab(params ?? routeParams);
  const controllerSnapshot = useSnapshot(controller);
  const parentSnapshot = useSnapshot(parent);
  // Many tabs read only one of the two (System reads no parentSnapshot at all).
  trackId(controllerSnapshot);
  trackId(parentSnapshot);
  return { controller, parent, controllerSnapshot, parentSnapshot };
}

// valtio treats a snapshot from which nothing was read as changed on every
// update, so an unused one re-renders its component on all live data. Reading
// the stable id gives it a minimal subscription instead.
export function trackId(controllerSnapshot) {
  return controllerSnapshot?.id;
}

// The received settings of one category as a plain (untracked) snapshot, for
// effects that sync the form from readback. Unlike useSnapshot, which re-renders
// only when a field that was read changes, this re-renders on any change inside
// the category: the form must also pick up fields it does not display, or the
// next save would send them back stale and overwrite another client's change.
export function useReceivedSettings(parent, category) {
  // Snapshot only the category: a snapshot of the whole parent would be rebuilt
  // on every live `infos` update.
  return useProxySelector(parent, (p) => {
    const settings = p.settings?.[category];
    return settings !== null && typeof settings === 'object' ? snapshot(settings) : settings;
  }, [category]);
}
